import React, { useState } from 'react';
import {
  TableRow,
  TableCell,
  IconButton,
  Typography,
  Box,
  Chip,
  Collapse,
  Table,
  TableHead,
  TableBody,
  CircularProgress,
  Stack,
} from '@mui/material';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  MoreVert as MoreVertIcon,
  Description as DescriptionIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { getStatusColor, formatFileName, formatFileType } from './utils';

const LeadRow = ({ row, onOpenMenu, processingId, onDownloadFile }) => {
  const [open, setOpen] = useState(false);
  const [downloadingIndex, setDownloadingIndex] = useState(null);

  const handleDownload = async (file, index) => {
    setDownloadingIndex(index);
    await onDownloadFile(file, index);
    setDownloadingIndex(null);
  };

  return (
    <React.Fragment>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          <Typography variant="subtitle2" fontWeight={500}>{row.name}</Typography>
        </TableCell>
        <TableCell>{row.email}</TableCell>
        <TableCell>{row.phone}</TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Chip
              label={row.last_status_log?.status?.name}
              color={getStatusColor(row.status?.status?.name)}
              size="small"
            />
            {row.status?.status?.name === 'Meeting' && row.meeting_info && (
              <Chip
                label={`Meeting @ ${new Date(row.meeting_info.start_datetime).toLocaleString()}`}
                size="small"
                color="warning"
                variant="outlined"
              />
            )}
          </Box>
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {row?.assignees?.length > 0 ? (
              row.assignees.map((assignee) => (
                <Chip
                  key={assignee.id}
                  label={assignee.name || assignee.username || 'Unnamed'}
                  variant="outlined"
                  size="small"
                // optional: color="primary" or color="default"
                />
              ))
            ) : (
              <Chip label="Unassigned" variant="outlined" size="small" />
            )}
          </Stack>
        </TableCell>
        <TableCell>{new Date(row.created_at).toLocaleDateString()}</TableCell>
        <TableCell sx={{ textAlign: 'center' }}>
          <IconButton
            onClick={(e) => onOpenMenu(e, row)}
            disabled={processingId === row.id}
          >
            {processingId === row.id ? <CircularProgress size={20} /> : <MoreVertIcon />}
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
              {/* Status Information */}
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                Status Information
              </Typography>
              <Table size="small" aria-label="status-info" sx={{ mb: 3 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Created At</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Created By</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Assignee</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <Chip
                        label={row.last_status_log?.status?.name}
                        color={getStatusColor(row.status?.status?.name)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {row.status?.description || 'No description available'}
                    </TableCell>
                    <TableCell>
                      {new Date(row.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {row.author?.name || 'System'}
                    </TableCell>
                    <TableCell>
                      {row?.last_status_log?.assignee?.name || 'Unassigned'}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              {/* Meeting Information */}
              {row.last_status_log?.status?.name === 'Meeting' && row.last_status_log?.meeting && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mt: 2 }}>
                    Meeting Information
                  </Typography>
                  <Table size="small" aria-label="meeting-info" sx={{ mb: 3 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Time Zone</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Start Date & Time</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>End Date & Time</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>{row?.last_status_log?.meeting?.time_zone || 'UTC'}</TableCell>
                        <TableCell>
                          {new Date(row.last_status_log?.meeting?.start_date_time).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          {new Date(row.last_status_log?.meeting?.end_date_time).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          {row.last_status_log?.meeting?.description || 'No meeting description'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={row?.last_status_log?.status?.name}
                            color={getStatusColor(row?.status?.meeting?.status?.name)}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </>
              )}

              {/* Attachments */}
              {(row?.attachments && row?.attachments?.length > 0) && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mt: 2 }}>
                    Attachments
                  </Typography>
                  <Table size="small" aria-label="attachments">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Uploaded At</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {row.attachments.slice(0, 7).map((file, idx) => {
                        const fileName = formatFileName(file, idx);
                        const filePath = file?.path;
                        const fileType = formatFileType(file, filePath);
                        const isDownloading = downloadingIndex === idx;

                        return (
                          <TableRow key={idx} hover>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <DescriptionIcon fontSize="small" color="action" />
                                <Typography variant="body2" noWrap>{fileName}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip label={fileType} size="small" />
                            </TableCell>
                            <TableCell>
                              {file?.created_at ? new Date(file.created_at).toLocaleDateString() : 'Unknown'}
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                {filePath && (
                                  <IconButton
                                    size="small"
                                    component="a"
                                    href={filePath}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title="View"
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                )}
                                {filePath && (
                                  isDownloading ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDownload(file, idx)}
                                      title="Download"
                                    >
                                      <DownloadIcon fontSize="small" />
                                    </IconButton>
                                  )
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {row.attachments.length > 7 && (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            <Typography variant="body2" color="primary">
                              + {row.attachments.length - 3} more attachments
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </>
              )}

              {(!row.attachments || row.attachments.length === 0) && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  No attachments available
                </Typography>
              )}


              {/* Issue Information */}
              {(row.issue) && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mt: 2 }}>
                    Lead Issue Information
                  </Typography>
                  <Table size="small" aria-label="meeting-info" sx={{ mb: 3 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>End Date & Time</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>{row?.issue?.title || 'N/A'}</TableCell>
                        <TableCell>
                          {row?.issue?.description}
                        </TableCell>
                        <TableCell>
                          {new Date(row.issue?.created_at).toLocaleString()}
                        </TableCell>
                        {/*
                        <TableCell>
                          {row.last_status_log?.meeting?.description || 'No meeting description'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={row?.last_status_log?.status?.name}
                            color={getStatusColor(row?.status?.meeting?.status?.name)}
                            size="small"
                          />
                        </TableCell> */}
                      </TableRow>
                    </TableBody>
                  </Table>
                </>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

export default LeadRow;